import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { DashboardWidgetComparisonType } from 'src/constants/dashboard';
import type {
  DashboardWidgetProps,
  pinType,
  UpstreamApiDashboardResult,
} from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';
import NumberDifference from 'src/components/dashboard/DashboardPage/components/NumberDifference';
import NumbersHeader from 'src/components/dashboard/DashboardPage/widgets/StackedBarWidget/NumbersHeader';
import { getFocalAndComparisonAggregates } from 'src/components/dashboard/helper';

import { allowedPins, getStackedBarRows } from './helper';

import { styles } from './index.styles';

export const STACKED_BAR_WIDGET_TEST_ID = 'stacked-bar-widget';

export const legendLabels = [
  'StronglyDisagree',
  'Disagree',
  'NeitherAgreeNorDisagree',
  'Agree',
  'StronglyAgree',
];

const StackedBarWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('dashboard');
  const { data, loading, errorMsg, refetch } = useStatistics();
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const { createStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );

  const sortedDataseriesContent = useMemo(() => {
    if (config.dataseries?.content?.bars?.length) {
      return [...config.dataseries.content.bars].sort((a, b) => {
        const pinNumA = parseInt(a.attribute.replace('pin', ''));
        const pinNumB = parseInt(b.attribute.replace('pin', ''));
        return pinNumB - pinNumA;
      });
    }
    return [];
  }, [config.dataseries]);

  const sortedPins = useMemo(() => {
    if (!sortedDataseriesContent.length) return undefined;

    return sortedDataseriesContent.reduce(
      (acc, pin, index) => {
        const attribute = pin?.attribute;
        acc[index] = allowedPins.includes(attribute) ? attribute : undefined;
        return acc;
      },
      {} as Record<number, pinType | undefined>,
    );
  }, [sortedDataseriesContent]);

  const pinColors = useMemo(() => {
    if (!sortedDataseriesContent.length) return [];

    return sortedDataseriesContent.reduce(
      (acc, pin, index) => {
        if (pin.color) {
          acc[index] = pin.color;
        }
        return acc;
      },
      {} as Record<number, string>,
    );
  }, [sortedDataseriesContent]);

  const pinFontColors = useMemo(() => {
    if (!sortedDataseriesContent.length) return [];

    return sortedDataseriesContent.reduce(
      (acc, pin, index) => {
        if (pin.fontColor) {
          acc[index] = pin.fontColor;
        }
        return acc;
      },
      {} as Record<number, string>,
    );
  }, [sortedDataseriesContent]);

  const customLegendLabels = useMemo(() => {
    if (sortedDataseriesContent.length) {
      return sortedDataseriesContent.map((pin) => pin.serieslabel);
    }
    return legendLabels;
  }, [sortedDataseriesContent]);

  const tableContent = useMemo(() => {
    if (config.dataseries?.content?.table?.length) {
      return config.dataseries.content.table;
    }
    return [];
  }, [config.dataseries]);

  const comparisonData =
    data && config.comparison_type === DashboardWidgetComparisonType.ANCESTOR
      ? data.ancestor_result
      : (data?.children_results[0] as UpstreamApiDashboardResult | undefined);

  const focalAndComparisonAggregates = getFocalAndComparisonAggregates(
    config,
    data,
  );

  const firstFocalAndComparisonAggregate =
    focalAndComparisonAggregates[0]?.focalAggregate;

  const hasExtraPin = useMemo(() => {
    if (sortedDataseriesContent.length) {
      return sortedDataseriesContent.some((pin) => pin.attribute === 'pin6');
    }
    return false;
  }, [sortedDataseriesContent]);

  const stackedBarRows = useMemo(
    () =>
      getStackedBarRows(
        focalAndComparisonAggregates,
        !!config.use_first_question_as_title,
        sortedPins as pinType[],
        tableContent?.[0]?.attribute,
        tableContent?.[1]?.attribute,
        hasExtraPin,
      ),
    [config, data],
  );

  if (loading || errorMsg) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="column"
          spacing={2}
          data-test-id="loading-stacked-bar"
          sx={{ p: 2 }}
        >
          <Skeleton variant="rectangular" height={40} />
          {config.questions.map((_, i) => (
            <Stack
              key={i}
              direction="row"
              spacing={1}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Skeleton variant="text" width="35%" />
              <Skeleton variant="rectangular" width="35%" height={30} />
              <Skeleton variant="text" width="10%" />
              <Skeleton variant="text" width="10%" />
              <Skeleton variant="text" width="10%" />
            </Stack>
          ))}
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, flexGrow: 1, maxWidth: '100%', position: 'relative' }}>
      {!!guidedTour && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {guidedTour}
        </Box>
      )}
      <Grid
        className="StackedBarWidget-root"
        container
        spacing={2}
        sx={styles.stackedBarWidget}
      >
        <Grid
          item
          lg={3}
          xl={4}
          xs={12}
          sx={{ alignItems: 'center', display: 'flex', justifyContent: 'left' }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 'bold', mb: { xs: 3, lg: 0 } }}
          >
            {config.use_first_question_as_title ? (
              <>
                {firstFocalAndComparisonAggregate?.label}:{' '}
                {firstFocalAndComparisonAggregate?.ptop2}% (
                <NumberDifference
                  sx={{ fontWeight: 'bold' }}
                  comparisonDiff={
                    firstFocalAndComparisonAggregate?.ptop2_yoyDiff
                  }
                />
                )
              </>
            ) : (
              config.name
            )}
          </Typography>
        </Grid>

        {upLg && (
          <>
            <Grid
              item
              lg={true}
              xs={12}
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ marginBottom: 2, ...styles.stackedBarLegend }}>
                {(config.dataseries?.content?.bars?.length
                  ? customLegendLabels
                  : legendLabels
                ).map((legendLabel, index) => (
                  <Typography
                    key={`${legendLabel}-${index}`}
                    variant="body2"
                    sx={[
                      styles.legendLabel(
                        index,
                        pinColors?.[index] ?? '',
                        sortedPins ? Object.keys(sortedPins).length : 5,
                      ),
                    ]}
                  >
                    {sortedPins ? legendLabel : t(`Answers.${legendLabel}`)}
                  </Typography>
                ))}
              </Box>
            </Grid>

            <NumbersHeader
              comparisonDataName={comparisonData?.org_units[0]?.name}
              tableContent={tableContent}
            />
          </>
        )}
      </Grid>

      <Grid container spacing={2}>
        {stackedBarRows.map((stackedBarRow, index) => (
          <Grid
            container
            item
            spacing={upLg ? 2 : 1}
            key={`${stackedBarRow.label}-${index}`}
          >
            <Grid
              item
              lg={3}
              xl={4}
              xs={12}
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'left',
              }}
            >
              <Typography variant="body2">{stackedBarRow.label}</Typography>
            </Grid>

            <Grid
              item
              lg={true}
              xs={12}
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box sx={styles.stackedBar}>
                {stackedBarRow.stack.map((percentage, index) => (
                  <Box
                    key={index}
                    sx={[
                      styles.stackElement(index, pinColors?.[index] ?? ''),
                      percentage ? {} : { display: 'none' },
                      { flexGrow: percentage },
                      pinFontColors?.[index]
                        ? { color: pinFontColors[index] }
                        : {},
                    ]}
                  >
                    {percentage}
                  </Box>
                ))}
              </Box>
            </Grid>

            {!upLg && <NumbersHeader />}

            <Grid container item xs={12} lg={3}>
              <Grid
                item
                xs={4}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={styles.number}>{stackedBarRow.fav}</Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {stackedBarRow.diff && (
                  <NumberDifference
                    comparisonDiff={stackedBarRow.diff}
                    sx={styles.number}
                  />
                )}
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={styles.number}>
                  {stackedBarRow.comparison}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default StackedBarWidget;
